import React, { useState, useEffect } from "react";
import { Custom, Item, Progress, Modal } from "components";
import { useApp } from "context";
import { Box, ButtonGroup, Grid, IconButton } from "@mui/material";
import { IAppointment, IBroadcast, IService } from "interfaces";
import { Constants, Enums, Helpers, Store } from "utils";
import BadgeIcon from "@mui/icons-material/Badge";

type Props = {
  sx?: any;
  state: boolean;
  opened: boolean;
  onClose?: () => void;
};

export const Appointment = (props: Props) => {
  const { locale, t, theme, service } = useApp();
  const [appointments, setAppointments] = useState([]);
  const [openProfile, setOpenProfile] = useState<boolean>(false);
  const [patientId, setPatientId] = useState<number | null>(null);

  const header = `${t.title.appointment} — ${
    props.state ? t.label.booked : t.label.waiting
  }`;

  const columns = [
    {
      field: "username",
      width: 150,
      headerName: t.label.name,
      renderCell: ({ row }: { row: IAppointment }) =>
        Helpers.replaceComma(row.username),
    },
    { field: "email", headerName: t.label.email, width: 200 },
    {
      field: "scheduledTime",
      width: 100,
      headerName: t.label.time,
      renderCell: ({ row }: { row: IAppointment }) =>
        new Date(row.scheduledTime).toLocaleTimeString(
          locale,
          Constants.TIME_FORMAT
        ),
    },
    {
      field: "profile",
      headerName: t.title.profile,
      width: 75,
      sortable: false,
      renderCell: ({ row }: { row: IAppointment }) => {
        return (
          <IconButton
            size="medium"
            color="inherit"
            onClick={() => {
              setPatientId(row.patientId);
              setOpenProfile(true);
            }}
          >
            <BadgeIcon />
          </IconButton>
        );
      },
    },
    {
      field: "serviceName",
      headerName: t.label.service,
      width: 125,
      valueGetter: ({ row }: { row: IAppointment }) => row.service?.serviceName,
    },
    {
      field: "booking",
      headerName: "Booking",
      width: 100,
      renderCell: ({ row }: { row: IAppointment }) => {
        return (
          <Custom.Button size="small" sx={serviceButtonStyle(row.walkIn)}>
            {row.walkIn ? t.title.patient.walk_in : t.title.patient.booked}
          </Custom.Button>
        );
      },
    },
    {
      field: "actions",
      width: 400,
      sortable: false,
      headerName: t.label.actions,
      renderCell: ({ row }: { row: IAppointment }) => {
        const hasCheckIn = Helpers.hasValue(row.checkInTime);
        const hasStartTime = Helpers.hasValue(row.startTime);
        const hasEndTime = Helpers.hasValue(row.endTime);
        return (
          <>
            <ButtonGroup>
              <Custom.Button
                size="small"
                sx={actionButtonStyle(hasCheckIn)}
                onClick={() =>
                  handleCheckIn(row.appointmentId, !hasCheckIn, row.patientId)
                }
              >
                {hasCheckIn ? t.action.undo_check_in : t.action.check_in}
              </Custom.Button>
              {hasCheckIn && (
                <>
                  <Custom.Button
                    size="small"
                    sx={actionButtonStyle(hasStartTime)}
                    onClick={() =>
                      handleStartTime(
                        row.appointmentId,
                        !hasStartTime,
                        row.patientId
                      )
                    }
                  >
                    {hasStartTime
                      ? t.action.undo_start_time
                      : t.action.start_time}
                  </Custom.Button>
                  {hasStartTime && (
                    <Custom.Button
                      size="small"
                      sx={actionButtonStyle(hasEndTime)}
                      onClick={() =>
                        handleEndTime(
                          row.appointmentId,
                          !hasEndTime,
                          row.patientId
                        )
                      }
                    >
                      {hasEndTime ? t.action.undo_end_time : t.action.end_time}
                    </Custom.Button>
                  )}
                </>
              )}
            </ButtonGroup>
          </>
        );
      },
    },
  ];

  function serviceButtonStyle(state: boolean) {
    return {
      color: state
        ? theme.color.status.success.text
        : theme.color.status.error.text,
      backgroundColor: state
        ? theme.color.status.success.color
        : theme.color.status.error.color,
    };
  }

  function actionButtonStyle(state: boolean) {
    return {
      color: state ? theme.color.status.error.text : null,
      backgroundColor: state ? theme.color.status.error.color : null,
    };
  }

  function fetchData() {
    Promise.all([service.appointment.today(true), service.service.all()]).then(
      ([result1, result2]) => {
        const [status, response] = result1;

        response.appointments.forEach((app: IAppointment) => {
          app.service = result2.find(
            (srv: IService) => srv.serviceId === app.serviceId
          );
        });

        setAppointments(response.appointments);
      }
    );
  }

  async function handleCheckIn(
    appointmentId: number,
    state: boolean,
    patientId: number
  ) {
    await service.appointment.updateCheckIn(appointmentId, state, patientId);
    fetchData();
  }

  async function handleStartTime(
    appointmentId: number,
    state: boolean,
    patientId: number
  ) {
    await service.appointment.updateStartTime(appointmentId, state, patientId);
    fetchData();
  }

  async function handleEndTime(
    appointmentId: number,
    state: boolean,
    patientId: number
  ) {
    await service.appointment.updateEndTime(appointmentId, state, patientId);
    fetchData();
  }

  useEffect(() => {
    fetchData();
  }, [props.opened]);

  return (
    <>
      <Custom.Modal
        header={header}
        opened={props.opened}
        onClose={props.onClose}
        sx={{ maxWidth: "100vw" }}
        action={
          <Custom.Button size="small" variant="outlined" onClick={fetchData}>
            {t.action.refresh}
          </Custom.Button>
        }
      >
        <Box sx={{ position: "relative" }}>
          {appointments && appointments.length > 0 ? (
            <>
              <Custom.DataGrid
                columns={columns}
                isLoading={false}
                rows={appointments}
                getRowId={(row: IAppointment) => row.appointmentId}
              />
            </>
          ) : (
            <Progress.NoRecord height={200} />
          )}
        </Box>
      </Custom.Modal>
      <Modal.Profile
        patientId={patientId}
        opened={openProfile}
        onClose={() => {
          setOpenProfile(false);
        }}
      />
    </>
  );
};
